import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, MessageCircle, Activity, User, Settings, Edit, MapPin, Briefcase, GraduationCap, CheckCircle, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Profile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    loadProfile();
    checkVerification();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.get();
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verification/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setIsVerified(data.verified);
    } catch (error) {
      console.error('Failed to check verification:', error);
    }
  };

  const levelColors = {
    beginner: 'bg-green-500',
    intermediate: 'bg-blue-500',
    advanced: 'bg-yellow-500',
    elite: 'bg-orange-500',
    college_pro: 'bg-red-500'
  };

  const levelLabels = {
    beginner: 'Beginner',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
    elite: 'Elite',
    college_pro: 'College/Pro'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="font-display font-bold text-xl uppercase text-gray-900">My Profile</h1>
          <Button 
            data-testid="edit-profile-btn"
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/profile/edit')}
            className="hover:bg-gray-100"
          >
            <Edit className="w-5 h-5 text-gray-600" />
          </Button>
        </div>
      </header>

      <main className="pt-16 px-4">
        <div className="max-w-lg mx-auto">
          {/* Profile Card */}
          <Card className="bg-white border-gray-100 shadow-sm overflow-hidden mb-6">
            {/* Main Photo */}
            <div className="aspect-square relative">
              <img
                src={profile?.photos?.[0] || user?.picture || 'https://via.placeholder.com/400'}
                alt={profile?.name || user?.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="font-display font-bold text-3xl text-white">
                    {profile?.name || user?.name}, {profile?.age}
                  </h2>
                  {/* Verification Badge */}
                  {isVerified && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500 text-white">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Verified</span>
                    </div>
                  )}
                </div>
                {profile?.location && (
                  <div className="flex items-center gap-1 text-white/80 mt-1">
                    <MapPin className="w-4 h-4" />
                    <span className="font-body text-sm">{profile.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="p-6 space-y-6">
              {/* Verification Status */}
              {!isVerified && (
                <button
                  onClick={() => navigate('/verify')}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <span className="font-body text-sm text-blue-700">Get verified to build trust</span>
                  </div>
                  <span className="text-blue-500 text-sm font-medium">Verify →</span>
                </button>
              )}

              {/* Sports */}
              <div>
                <h3 className="font-display font-bold text-sm uppercase text-gray-400 mb-3">Sports</h3>
                <div className="flex flex-wrap gap-2">
                  {profile?.sports?.map((sport, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100"
                    >
                      <span className={`w-2 h-2 rounded-full ${levelColors[sport.level]}`} />
                      <span className="font-body text-sm text-gray-700">{sport.sport}</span>
                      <span className="font-body text-xs text-gray-500">
                        {levelLabels[sport.level]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bio */}
              {profile?.bio && (
                <div>
                  <h3 className="font-display font-bold text-sm uppercase text-gray-400 mb-3">About</h3>
                  <p className="font-body text-sm text-gray-700">{profile.bio}</p>
                </div>
              )}

              {/* Work & Education */}
              {(profile?.job_title || profile?.work || profile?.school) && (
                <div>
                  <h3 className="font-display font-bold text-sm uppercase text-gray-400 mb-3">Details</h3>
                  <div className="space-y-2">
                    {(profile?.job_title || profile?.work) && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="font-body text-gray-700">{profile.job_title}{profile.work && ` at ${profile.work}`}</span>
                      </div>
                    )}
                    {profile?.school && (
                      <div className="flex items-center gap-2 text-sm">
                        <GraduationCap className="w-4 h-4 text-gray-400" />
                        <span className="font-body text-gray-700">{profile.school}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Prompts */}
              {profile?.prompts?.length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-sm uppercase text-gray-400 mb-3">Prompts</h3>
                  <div className="space-y-4">
                    {profile.prompts.map((prompt, i) => (
                      <div key={i} className="p-4 rounded-xl bg-gray-50">
                        <p className="font-body text-xs text-gray-500 mb-1">{prompt.question}</p>
                        <p className="font-body text-sm text-gray-700">{prompt.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Photo Gallery */}
          {profile?.photos?.length > 1 && (
            <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4">
                <h3 className="font-display font-bold text-sm uppercase text-gray-400 mb-3">Photos</h3>
                <div className="grid grid-cols-3 gap-2">
                  {profile.photos.slice(1).map((photo, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden">
                      <img src={photo} alt={`Photo ${i + 2}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-2 flex justify-around">
          <button 
            data-testid="nav-discover"
            onClick={() => navigate('/discover')} 
            className="flex flex-col items-center gap-1 p-2 text-gray-400"
          >
            <Activity className="w-6 h-6" />
            <span className="text-xs font-body">Discover</span>
          </button>
          <button 
            data-testid="nav-matches"
            onClick={() => navigate('/matches')} 
            className="flex flex-col items-center gap-1 p-2 text-gray-400"
          >
            <Heart className="w-6 h-6" />
            <span className="text-xs font-body">Matches</span>
          </button>
          <button 
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 p-2 text-primary"
          >
            <User className="w-6 h-6" />
            <span className="text-xs font-body">Profile</span>
          </button>
          <button 
            data-testid="nav-settings"
            onClick={() => navigate('/settings')} 
            className="flex flex-col items-center gap-1 p-2 text-gray-400"
          >
            <Settings className="w-6 h-6" />
            <span className="text-xs font-body">Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );
};
